import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Slider, Icon } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { clampOffset, clampScale, initialScaleToCover } from './cropGeometry';
import { exportCroppedJpeg } from './exportCrop';
import './CropModal.less';

const CROP_DIAMETER = 280;
const ARROW_STEP = 4;

/**
 * Accessible circular crop modal. Cancel restores the candidate snapshot from when the modal opened.
 */
function ProfilePictureCropModal({
  visible,
  image,
  invokingElement,
  onCancel,
  onUsePicture,
}) {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [minScale, setMinScale] = useState(1);
  const [exporting, setExporting] = useState(false);

  const resetToFit = useCallback(() => {
    if (!image) return;
    const nextScale = initialScaleToCover(image.naturalWidth, image.naturalHeight, CROP_DIAMETER);
    setMinScale(nextScale);
    setScale(nextScale);
    setOffsetX(0);
    setOffsetY(0);
  }, [image]);

  useEffect(() => {
    if (visible && image) {
      resetToFit();
    }
  }, [visible, image, resetToFit]);

  useEffect(() => {
    if (!visible || !invokingElement) return undefined;
    const previouslyFocused = document.activeElement;
    const timer = setTimeout(() => {
      const dialog = viewportRef.current && viewportRef.current.closest('.ant-modal');
      const focusable = dialog && dialog.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) focusable.focus();
    }, 50);

    const onKeyDownTrap = event => {
      if (event.key !== 'Tab') return;
      const dialog = viewportRef.current && viewportRef.current.closest('.ant-modal');
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(el => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDownTrap);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDownTrap);
      if (invokingElement && typeof invokingElement.focus === 'function') {
        invokingElement.focus();
      } else if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [visible, invokingElement]);

  const applyOffset = useCallback(
    (nextX, nextY, nextScale = scale) => {
      if (!image) return;
      const clampedScale = clampScale(nextScale, image.naturalWidth, image.naturalHeight, CROP_DIAMETER);
      const clamped = clampOffset(
        nextX,
        nextY,
        image.naturalWidth,
        image.naturalHeight,
        clampedScale,
        CROP_DIAMETER,
      );
      setScale(clampedScale);
      setOffsetX(clamped.offsetX);
      setOffsetY(clamped.offsetY);
    },
    [image, scale],
  );

  const onPointerDown = event => {
    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offsetX,
      originY: offsetY,
    };
    if (event.target.setPointerCapture) {
      event.target.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = event => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    applyOffset(dragRef.current.originX + dx, dragRef.current.originY + dy);
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const onWheel = event => {
    event.preventDefault();
    if (!image) return;
    const delta = event.deltaY > 0 ? -0.05 : 0.05;
    applyOffset(offsetX, offsetY, scale + delta);
  };

  const onKeyDown = event => {
    if (!image) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      applyOffset(offsetX - ARROW_STEP, offsetY);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      applyOffset(offsetX + ARROW_STEP, offsetY);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      applyOffset(offsetX, offsetY - ARROW_STEP);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      applyOffset(offsetX, offsetY + ARROW_STEP);
    }
  };

  const handleUsePicture = async () => {
    if (!image) return;
    setExporting(true);
    try {
      const blob = await exportCroppedJpeg(image, {
        scale,
        offsetX,
        offsetY,
        cropDiameter: CROP_DIAMETER,
      });
      onUsePicture(blob);
    } finally {
      setExporting(false);
    }
  };

  const scaledWidth = image ? image.naturalWidth * scale : 0;
  const scaledHeight = image ? image.naturalHeight * scale : 0;

  return (
    <Modal
      visible={visible}
      title={t('common:profilePicture.crop.title')}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={420}
      className="profile-picture-crop-modal"
    >
      <div
        ref={viewportRef}
        className="profile-picture-crop-viewport"
        role="application"
        aria-label={t('common:profilePicture.crop.viewportLabel')}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onWheel={onWheel}
      >
        <div
          className="profile-picture-crop-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {image ? (
            <img
              src={image.src}
              alt=""
              draggable={false}
              className="profile-picture-crop-image"
              style={{
                width: scaledWidth,
                height: scaledHeight,
                transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))`,
              }}
            />
          ) : null}
          <div className="profile-picture-crop-mask" aria-hidden="true" />
        </div>
      </div>

      <div className="profile-picture-crop-zoom">
        <Button
          type="link"
          aria-label={t('common:profilePicture.crop.zoomOut')}
          onClick={() => applyOffset(offsetX, offsetY, scale - 0.1)}
        >
          <Icon type="minus" />
        </Button>
        <Slider
          min={minScale}
          max={Math.max(minScale * 4, minScale + 0.01)}
          step={0.01}
          value={scale}
          onChange={value => applyOffset(offsetX, offsetY, value)}
          aria-label={t('common:profilePicture.crop.zoom')}
        />
        <Button
          type="link"
          aria-label={t('common:profilePicture.crop.zoomIn')}
          onClick={() => applyOffset(offsetX, offsetY, scale + 0.1)}
        >
          <Icon type="plus" />
        </Button>
      </div>

      <div className="profile-picture-crop-actions">
        <Button onClick={resetToFit}>{t('common:profilePicture.crop.reset')}</Button>
        <Button onClick={onCancel}>{t('common:profilePicture.crop.cancel')}</Button>
        <Button type="primary" loading={exporting} onClick={handleUsePicture}>
          {t('common:profilePicture.crop.usePicture')}
        </Button>
      </div>
    </Modal>
  );
}

ProfilePictureCropModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  image: PropTypes.object,
  invokingElement: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onUsePicture: PropTypes.func.isRequired,
};

ProfilePictureCropModal.defaultProps = {
  image: null,
  invokingElement: null,
};

export default ProfilePictureCropModal;
